import {
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Text,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { AddressCard, Select } from '@acp/core-ui';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useIntegratedProperty } from '../hooks/use-integrated-property';
import { useUpdateIntegratedProperty } from '../hooks/use-update-integrated-property';

const schema = z.object({
    nonResidentialPropertyType: z.string().nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

export function ChangeResidentialPropertyModal({
    agencyPortfolioPropertyId,
    isOpen,
    onClose,
}: {
    agencyPortfolioPropertyId: number;
    isOpen: boolean;
    onClose: () => void;
}) {
    const toast = useToast();
    const property = useIntegratedProperty(agencyPortfolioPropertyId, {
        isEnabled: isOpen,
    });

    const form = useForm<FormValues>({
        resolver: zodResolver(schema.superRefine((arg, ctx) => {
            if (property.data?.isResidentialProperty === true
                && (arg.nonResidentialPropertyType ?? null) === null) {
                ctx.addIssue({
                    message: 'Property type is required',
                    code: 'custom',
                    path: ['nonResidentialPropertyType'],
                });
            }
        })),
        defaultValues: {
            nonResidentialPropertyType: null,
        },
    });

    const handleClose = () => {
        form.reset();
        onClose();
    };

    const mutation = useUpdateIntegratedProperty(agencyPortfolioPropertyId, {
        onSuccess: () => {
            handleClose();
            toast({
                title: 'Property updated',
                status: 'success',
                description: 'Property has been updated successfully',
            });
        },
    });

    const handleSubmit = (values: FormValues) => {
        if (property.data?.isResidentialProperty === true) {
            mutation.mutate({
                isResidentialProperty: false,
                nonResidentialPropertyType: values.nonResidentialPropertyType!,
            });
            return;
        }

        mutation.mutate({
            isResidentialProperty: true,
        });
    };

    return (
        <Modal size="lg" isOpen={isOpen} onClose={handleClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Change residential rental property status</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {property.isLoading && (
                        <HStack justifyContent="center" py={10}>
                            <Spinner />
                        </HStack>
                    )}
                    {
                        property.data !== undefined && (
                            <>
                                <AddressCard
                                    isLoading={property.isLoading}
                                    address={property.data.address}
                                    mb={4}
                                />
                                {
                                    property.data.isResidentialProperty === true && (
                                        <VStack>
                                            <Text>
                                                Changing this property to a non-residential
                                                rental property will exclude
                                                it from any compliance reporting.
                                            </Text>
                                            <Text>
                                                You can change this setting at
                                                any time through the property page.
                                            </Text>
                                            <Text>
                                                Are you sure you want to change this
                                                property to a non-residential rental property?
                                            </Text>
                                            <Controller
                                                control={form.control}
                                                name="nonResidentialPropertyType"
                                                render={({ field, fieldState }) => (
                                                    <FormControl
                                                        isRequired
                                                        isInvalid={fieldState.error !== undefined}
                                                    >
                                                        <FormLabel>
                                                            What type of property is this?
                                                        </FormLabel>
                                                        <Select
                                                            options={[
                                                                { label: 'Commercial', value: 'commercial' },
                                                                { label: 'Strata', value: 'industrial' },
                                                                { label: 'Other', value: 'other' },
                                                            ]}
                                                            placeholder="Select property type"
                                                            onChange={
                                                                (v) => field.onChange(v?.value)
                                                            }
                                                            isClearable={false}
                                                            isSearchable={false}
                                                        />
                                                        <FormErrorMessage>
                                                            {fieldState.error?.message}
                                                        </FormErrorMessage>
                                                    </FormControl>
                                                )}
                                            />
                                        </VStack>
                                    )
                                }

                                {
                                    property.data.isResidentialProperty === false && (
                                        <VStack>
                                            <Text>
                                                Changing this property to a residential rental
                                                property will include it in
                                                any compliance reporting.
                                            </Text>
                                            <Text>
                                                You can change this setting at
                                                any time through the property page.
                                            </Text>
                                            <Text>
                                                Are you sure you want to change
                                                this property to a residential rental property?
                                            </Text>
                                        </VStack>
                                    )
                                }
                            </>
                        )
                    }
                </ModalBody>

                <ModalFooter justifyContent="space-between">
                    <Button variant="ghost" colorScheme="muted" mr={3} onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={form.handleSubmit(handleSubmit)}
                        disabled={property.isLoading}
                        isLoading={mutation.isPending}
                    >
                        Change property
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
